/**
 * @module ui/facet-search-sticky
 * @description Controls the Search Results page, facet-search sticky components.
 */
import ee from "lib/eventemitter";
import { pageYOffset } from "utils/window-events";
import { qs } from "utils/utils";
import Stickyfill from "stickyfilljs";

let header; // element
let headerHeight; // element height
let searchForm; // element
let searchFormHeight; // element height
let searchTabs; // element
let searchSummary; // element
let searchHr; // element

/**
 * @method init
 * @description Additional initialization for this particular header type
 * @param {HTMLElement} headerElement - The header element
 */
export default function init(headerElement) {
  header = headerElement;
  if (!header) {
    return;
  }

  // search results page elements
  searchForm = qs(".js-search-form-sticky");
  searchTabs = qs(".js-search-tabs-sticky");
  searchHr = qs(".js-search-results-tabs-hr");

  const onSearchPage = searchForm && searchTabs && searchHr;
  if (!onSearchPage) {
    return;
  }

  searchSummary = qs(".l-search-summary");

  headerHeight = Math.floor(header.getBoundingClientRect().height);
  searchFormHeight = Math.floor(searchForm.getBoundingClientRect().height);
  Stickyfill.add([searchForm, searchTabs]);

  // store previous scroll position, to determine direction.
  let previousScrollPos = pageYOffset(); // current scroll position

  const onPageScroll = scrollPos => {
    const scrolled = Math.abs(previousScrollPos - scrollPos);
    if (scrolled <= 0) {
      return;
    }

    const scrollingDown = scrollPos >= previousScrollPos;

    window.requestAnimationFrame(() => {
      // Position the search form div and search tabs div
      let formPosY = 0;
      let tabsPosY = searchFormHeight;
      if (!scrollingDown) {
        formPosY += headerHeight;
        tabsPosY += headerHeight;
      }
      positionSearch(formPosY, tabsPosY);

      // Show or hide the horizontal ruler, that appears below the tabs.
      const searchSummaryBottom = Math.floor(searchSummary.getBoundingClientRect().bottom);
      let positionY = searchSummaryBottom - searchFormHeight;
      if (!scrollingDown) {
        positionY -= headerHeight;
      }
      const action = positionY < 0 ? "add" : "remove";
      searchHr.classList[action]("c-search-results-tabs-hr--display");
    });

    previousScrollPos = scrollPos;
  };
  ee.on("windowScroll.throttled", onPageScroll);
}

/**
 * @method positionSearch
 * @description Position the sticky elements for the search form
 * @param {Number} form - top form input element
 * @param {Number} tabs - bottom results tabs element
 */
function positionSearch(form, tabs) {
  const windowHeight = window.innerHeight; // viewport height
  const stickyElHeight = headerHeight + searchFormHeight; // height of the search bar and tabs combined
  const removeSticky = stickyElHeight * 2 >= windowHeight; // if the sticky elements take up at least half of the screen

  if (removeSticky) {
    // when the viewport height is too small to show search results,
    // make the sticky search bar and tabs unsticky
    const styles = "top: 0; position: relative;";
    searchForm.style.cssText = styles;
    searchTabs.style.cssText = styles;
  } else {
    // otherwise, reset the search bar and tabs styles
    searchForm.style.cssText = "";
    searchTabs.style.cssText = "";

    // Since we are changing the sticky top position value,
    // we also need to remove and re-add the elements. Refresh won't work.
    searchForm.style.transition = searchTabs.style.transition = ""; // needed for IE 11
    Stickyfill.remove([searchForm, searchTabs]);

    searchForm.style.top = `${form}px`;
    searchTabs.style.top = `${tabs}px`;

    Stickyfill.add([searchForm, searchTabs]);
    searchForm.style.transition = searchTabs.style.transition = "top 0.25s linear 0s";
  }
}
